import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useCallback } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import ExternalLink from "../../../components/ExternalLink";
import InfoIcon from "../../../components/icons/InfoIcon";
import InfoItems from "../../../components/InfoItems";
import TruncatedText from "../../../components/TruncatedText";
import { useHotspotMakers } from "../../../context/hotspotMakers";
import { useHotspots } from "../../../context/hotspots";
import { formatDateToLocale } from "../../../utils/dates";
import { formatHotspotPlace } from "../../../utils/hotspotUtils";
import { Hotspot } from "../../../utils/types";
import useMapConfig from "../../../utils/useMapConfig";

const HotspotInfoTab: React.FC<RouteComponentProps<{ id: string }>> = () => {
  const { getHotspotMakerName } = useHotspotMakers();
  const { currentHotspot, walletHotspotCountMap, setCurrenHotspotId } =
    useHotspots();
  const renderDistance = useMapConfig((state) => state.renderDistance);

  const getInfoItems = useCallback(
    (hotspot: Hotspot) => {
      const {
        id,
        owner,
        place,
        lat,
        lng,
        timestamp_added,
        last_update,
        last_assertion,
        elevation,
        gain,
        deny_list
      } = hotspot;
      const walletHotspotsCount = walletHotspotCountMap[owner] || 0;
      return [
        {
          label: "Hotspot id",
          value: (
            <TruncatedText
              text={id}
              label="hotspot address"
              truncateLength={30}
            />
          )
        },
        {
          label: `Wallet ${
            walletHotspotsCount
              ? `(${walletHotspotsCount} hotspot${
                  walletHotspotsCount === 1 ? "" : "s"
                })`
              : ""
          }`,
          value: (
            <TruncatedText
              text={owner}
              label="wallet address"
              truncateLength={30}
            />
          )
        },
        {
          label: "Blockchain data",
          value: (
            <ExternalLink
              text="Helium Blockchain Explorer"
              url={`https://explorer.helium.com/hotspots/${id}`}
            />
          )
        },
        {
          label: "Location",
          value: (
            <>
              {formatHotspotPlace(place)}
              {!!lat && !!lng && (
                <ExternalLink
                  text="Google Earth"
                  url={`https://earth.google.com/web/search/${lat},${lng}/`}
                />
              )}
            </>
          )
        },
        { label: "Added to blockchain", value: renderDate(timestamp_added) },
        {
          label: "Last refreshed",
          value: last_update ? moment.unix(last_update).utc().fromNow() : null
        },
        { label: "Last location assertion", value: renderDate(last_assertion) },
        {
          label: (
            <>
              Installation height
              <Tooltip overlay="Antenna height above the ground">
                <InfoIcon className="ml-1 inline-flex h-4 w-4" />
              </Tooltip>
            </>
          ),
          value: renderDistance(elevation, false)
        },
        { label: "Antenna gain", value: `${gain / 10}dBi` },
        {
          label: "Maker",
          value: (
            <Link
              onClick={() => setCurrenHotspotId("")}
              className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
              to={`/hotspots/makers/${hotspot.payer}`}
            >
              {getHotspotMakerName(hotspot.payer)}
            </Link>
          )
        },
        {
          label: (
            <>
              Denylist{" "}
              <Tooltip
                overlay={
                  <>
                    Read more about the denylist
                    <ExternalLink
                      className="inline-flex text-xs ml-1"
                      text="here"
                      url="https://github.com/helium/HIP/blob/master/0040-validator-denylist.md"
                    />
                  </>
                }
              >
                <InfoIcon className="inline-flex h-4 w-4" />
              </Tooltip>
            </>
          ),
          value: deny_list ? "Included" : "Not included"
        }
      ];
    },
    [
      getHotspotMakerName,
      renderDistance,
      setCurrenHotspotId,
      walletHotspotCountMap
    ]
  );

  if (!currentHotspot) return null;

  return <InfoItems infoItems={getInfoItems(currentHotspot)} />;
};

export default HotspotInfoTab;

//
// Utils
//

export const renderDate = (timestamp: number | null) => {
  if (!timestamp) return;

  return `${formatDateToLocale(
    moment.unix(timestamp).utc().toLocaleString()
  )} (${moment.unix(timestamp).fromNow()})`;
};
